import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Form } from "react-bootstrap";
import swal from "sweetalert";
import BASE_URL from "../../assets/global/url";
import { Link, useNavigate, useParams } from "react-router-dom";
import useDecodeToken from "../../hooks/customHook/useDecodeToken";
// import DatePicker from "react-datepicker";
import CustomDatePicker from "../../components/CustomDatePicker";
import SharedTypedDateInput from "../../components/inputs/TypedDateInput";
import FixedAssetPaymentModal from "./components/FixedAssetPaymentModal";
import { FixedAssetJournalModal } from "./components/FixedAssetJournalModal";
import { FixedAssetPreviewWidget } from "./components/FixedAssetPreviewWidget";
import { FixedAssetForecastModal } from "./components/FixedAssetForecastModal";
import { formatCurrency } from "../../utils/numberFormatter";
import { checkCutoffDate } from "../../utils/cutoffDateValidation";

// Custom input for DatePicker: typed as MM/dd/yyyy and shown as Oct/10/2026
// (components/inputs/TypedDateInput), or picked from the calendar. Only a
// complete date reaches handleDateChange, so the cutoff check doesn't run on a
// half-typed date. Adds the padding option and the year list reset on click.
// Used for the Depreciation Date and passed to the Create Payment modal.
const TypedDateInput = React.forwardRef(
  (
    {
      value,
      onClick,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      generateYears,
      padding,
      disabled,
    },
    ref,
  ) => {
    return (
      <SharedTypedDateInput
        className={`form-control ${padding || "p-2"} w-100`}
        style={{ cursor: disabled ? "pointer" : "text" }}
        onClick={(e) => {
          if (onClick) onClick(e);

          let selectedYear = new Date().getFullYear();
          if (value) {
            const parsed = new Date(value);
            // Extract year safely if value is a valid date
            if (!isNaN(parsed.getTime())) {
              selectedYear = parsed.getFullYear();
            }
          }

          // Reset/Initialize Year List based on selected date
          if (generateYears && typeof generateYears === "function") {
            generateYears(selectedYear);
          }
        }}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        value={value}
        ref={ref}
        disabled={disabled}
        required
      />
    );
  },
);

const CreateFixedAsset = ({ authrztn }) => {
  const MAX_MONTHS_TO_PAY = 1000;
  const [depreciationError, setDepreciationError] = useState("");
  const [monthsError, setMonthsError] = useState("");
  const userLoggedID = useDecodeToken();
  const navigate = useNavigate();
  const { id } = useParams();
  // const [firstRender, setFirstRender] = useState(true);
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);

  const [transactionCode, setTransactionCode] = useState("");
  const [date, setDate] = useState();
  const [expenses_id, setExpenses_id] = useState("");
  const [costPerUnit, setCostPerUnit] = useState("");
  const [isCostPerUnitFocused, setIsCostPerUnitFocused] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [totalCost, setTotalCost] = useState("");
  const [monthsToPay, setMonthsToPay] = useState("");
  const [depreciationAmount, setDepreciationAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [productName, setProductName] = useState("");
  const [currencyList, setCurrencyList] = useState([]);
  const [currencyId, setCurrencyId] = useState(
    "11111111-1111-1111-1111-111111111111",
  );
  const [currencyName, setCurrencyName] = useState("PHP");
  // const [products, setProducts] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [allowZeroDepreciation, setAllowZeroDepreciation] = useState(false);

  const [status, setStatus] = useState("");
  const [isCutoffPosted, setIsCutoffPosted] = useState(false);
  const [edit, setEdit] = useState(true);

  // For Modals
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showJournalModal, setShowJournalModal] = useState(false);

  // States for approved fixed asset
  // const [approvedForecast, setApprovedForecast] = useState([]);
  // const [approvedForecastValue, setApprovedForecastValue] = useState(0);
  const [expensesValue, setExpensesValue] = useState(0);
  // const [paidCount, setPaidCount] = useState(0);
  // const [selectedFixedAsset, setSelectedFixedAsset] = useState([]);

  // Form validations state
  const [validated, setValidated] = useState(false);
  const [validatedFormCheck, setValidatedFormCheck] = useState(false);

  // For accounts dropdown options
  // const [subject2List, setSubject2List] = useState([]);
  // const [subject3List, setSubject3List] = useState([]);

  const removeComma = (num) => parseFloat(String(num).replace(/,/g, ""));

  // Get fixed asset currency rate
  const currencyRate = currencyList?.find(
    (item) => item.id === currencyId,
  )?.currency_rate;

  const onInputFloat = (e) => {
    e.target.value = e.target.value.replace(/[^0-9.]/g, "");
  };

  const calculateTotalCost = () => {
    setTotalCost(
      (removeComma(costPerUnit) || 0) * (removeComma(quantity) || 0),
    );
  };

  const fetchLastCode = () => {
    axios
      .get(BASE_URL + "/fixedasset/getCode")
      .then((res) => {
        setTransactionCode(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Fetch fixed asset with specific id for viewing/edit
  const fetchFixedAsset = async (id) => {
    try {
      const res = await axios.get(`${BASE_URL}/fixedasset/getFixedAssetById`, {
        params: {
          id,
        },
      });

      setTransactionCode(res.data.transaction_code);
      setDate(res.data.date_depreciated);
      setExpenses_id(res.data.expenses_id);
      setCurrencyName(res.data.currency.currency_name);
      setCostPerUnit(res.data.cost_per_unit);
      setQuantity(res.data.quantity);
      setTotalCost(res.data.total_cost);
      setMonthsToPay(res.data.static_months_to_pay);
      setDepreciationAmount(res.data.depreciation_amount);
      setRemarks(res.data.remarks);
      setStatus(res.data.status);
      setIsCutoffPosted(res.data.isPosted);
      setProductName(res.data.product_name);
      setCurrencyId(res.data.currency_id);
      setEdit(false);
      setExpensesValue(res.data.expensesAmount);

      // fetchApprovedForecast(id, res.data.static_months_to_pay);

      setAllowZeroDepreciation(
        res.data.static_months_to_pay === 0 &&
          res.data.depreciation_amount === 0,
      );

      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error(error);
    }
  };

  // const fetchAssets = () => {
  //   axios
  //     .get(BASE_URL + "/fixedasset/fetchExpensesProducts", {
  //       params: { id: 0 },
  //     })
  //     .then((res) => {
  //       let newExpensesData = [];
  //       // console.log("Raw response data:", res.data);

  //       res.data.forEach((data) => {
  //         // console.log("Checking item:", data);

  //         // Case 1: No fixed asset exists or empty array
  //         if (!data.fixed_assets || data.fixed_assets.length === 0) {
  //           // console.log("Case 1: No fixed assets or empty array");
  //           newExpensesData.push(data);
  //         }
  //         // Case 2: Fixed asset exists with empty array
  //         else if (
  //           Array.isArray(data.fixed_assets) &&
  //           data.fixed_assets.length === 0
  //         ) {
  //           // console.log("Case 2: Empty fixed assets array");
  //           newExpensesData.push(data);
  //         }
  //         // Case 3: Fixed asset exists with Rejected status
  //         else if (
  //           Array.isArray(data.fixed_assets) &&
  //           data.fixed_assets.length > 0
  //         ) {
  //           const lastAsset = data.fixed_assets[data.fixed_assets.length - 1];
  //           if (lastAsset.status === "Rejected") {
  //             // console.log("Case 3: Last asset has Rejected status");
  //             newExpensesData.push(data);
  //           }
  //         }
  //       });

  //       // console.log("Filtered data:", newExpensesData);
  //       setProducts(newExpensesData);
  //     })
  //     .catch((error) => {
  //       console.error("Error fetching assets:", error);
  //     });
  // };

  // Fetch all approved fixed asset forecast
  // const fetchApprovedForecast = async (id, monthsToPay) => {
  //   try {
  //     const res = await axios.get(
  //       `${BASE_URL}/fixedasset/getApprovedForecast`,
  //       {
  //         params: {
  //           id,
  //         },
  //       },
  //     );

  //     const { fixedAssetForecast, paidItemCount, totalCost, expensesAmount } =
  //       res.data;
  //     const sortedForecastDate = fixedAssetForecast?.sort(
  //       (a, b) => new Date(a.date) - new Date(b.date),
  //     );
  //     const total = fixedAssetForecast.reduce(
  //       (acc, curr) => (curr.isPaid ? acc + curr.amount : acc),
  //       0,
  //     );

  //     setApprovedForecast(sortedForecastDate);
  //     setApprovedForecastValue(totalCost - total);
  //     setExpensesValue(expensesAmount);
  //     setPaidCount(monthsToPay - paidItemCount); // Count of rows where isPaid is true
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  // Handle fixed asset approval
  const handleApproveFixedAsset = () => {
    swal({
      title: "Approve this fixed asset?",
      text: "",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        axios
          .post(`${BASE_URL}/fixedasset/approveFixedAsset`, {
            id,
            userLoggedID,
          })
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Success",
                text: "Fixed asset approved successfully",
                icon: "success",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              }).then(() => {
                fetchFixedAsset(id);
                setEdit(false);
                setShow(false);
              });
            }
          })
          .catch((error) => {
            if (error.response.status === 401) {
              swal({
                icon: "error",
                // title: "Please contact your support",
                title: error.response.data.title,
                text: error.response.data.message,
              }); // test 1
            } else {
              console.error(error);
            }
          });
      }
    });
  };

  // Handle fixed asset rejection
  const handleRejectFixedAsset = () => {
    swal({
      title: "Reject this fixed asset?",
      text: "",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        axios
          .post(`${BASE_URL}/fixedasset/rejectFixedAsset`, null, {
            params: {
              id,
              userLoggedID,
            },
          })
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Success",
                text: "Fixed asset rejected successfully",
                icon: "success",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              }).then(() => {
                fetchFixedAsset(id);
                setEdit(false);
                setShow(false);
              });
            }
          })
          .catch((error) => {
            if (error.response.status === 401) {
              swal({
                icon: "error",
                // title: "Please contact your support",
                title: error.response.data.title,
                text: error.response.data.message,
              }); // test 1
            } else {
              console.log(error);
            }
          });
      }
    });
  };

  // Soft delete the fixed asset; its payments and depreciation are reverted
  const handleDeleteFixedAsset = async () => {
    const confirmDelete = await swal({
      icon: "warning",
      title: "Delete Fixed Asset?",
      text: "All of its payments (bank, check and cash) will be returned to their accounts and its depreciation reverted. This cannot be undone.",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });

    if (!confirmDelete) return;

    try {
      await axios.delete(
        `${BASE_URL}/fixedasset/v2/deleteFixedAsset/${id}/${userLoggedID}`,
      );

      await swal({
        icon: "success",
        title: "Fixed Asset Deleted",
        text: "The fixed asset has been deleted and its payments and depreciation reverted.",
      });

      navigate("/accounting/fixedAsset");
    } catch (error) {
      console.error(error);

      swal({
        icon: "error",
        title: error.response?.data?.title || "Something went wrong",
        text:
          error.response?.data?.message ||
          "Please contact your support immediately",
      });
    }
  };

  // Calculate and divide fixed asset forecast by months
  const handleCheckForecast = () => {
    const forecastData = [];
    const startDate = new Date(date); // Convert the date string to a Date object
    const monthsToForecast = Math.ceil(parseFloat(monthsToPay)); // Ensure monthsToPay is rounded up

    // Set a fixed depreciation amount
    const fixedDepreciationAmount = parseFloat(
      String(depreciationAmount).replace(/,/g, ""),
    ); // Change this to your desired fixed amount

    // If zero depreciation is allowed, don't generate forecast
    if (allowZeroDepreciation) {
      setForecast([]);
      setShow(true);
      return;
    }

    for (let i = 1; i <= monthsToForecast; i++) {
      const nextMonth = new Date(startDate);
      nextMonth.setMonth(startDate.getMonth() + i); // Add months
      forecastData.push({
        date: nextMonth.toISOString().split("T")[0], // Format to YYYY-MM-DD
        amount: fixedDepreciationAmount, // Use the fixed depreciation amount
      });
    }

    // Calculate total depreciation
    const totalDepreciation = fixedDepreciationAmount * monthsToForecast;

    // Calculate remaining amount
    const remainingAmount = totalCost - totalDepreciation;

    // If there's a remaining amount, adjust the last month's amount
    if (remainingAmount < 0) {
      // If total depreciation exceeds total cost, adjust the last entry
      forecastData[forecastData.length - 1].amount += remainingAmount; // Adjust last month's amount
    } else if (remainingAmount > 0) {
      // If there's a remaining amount, add it as an additional forecast entry
      const nextMonth = new Date(startDate);
      nextMonth.setMonth(startDate.getMonth() + monthsToForecast); // Add one more month
      forecastData.push({
        date: nextMonth.toISOString().split("T")[0], // Format to YYYY-MM-DD
        amount: remainingAmount, // Remaining amount
      });
    }

    setForecast(forecastData);
    setShow(true);
  };

  // Form Save Button
  const openModal = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
    } else {
      if (Number(totalCost).toFixed(2) === "0.00") {
        swal({
          icon: "warning",
          title: "Invalid Total Cost",
          text: "The total cost must be greater than zero. Please review your entries.",
        });
        return;
      }

      if (
        monthsToPay === 0 &&
        depreciationAmount === 0 &&
        !allowZeroDepreciation
      ) {
        swal({
          icon: "warning",
          title: "Invalid Months to Pay and Depreciation Amount",
          text: "Months to Pay and Depreciation Amount should be greater than zero.",
        });
        return;
      }

      // Prevent invalid value when Months to Pay is less than 1, ex: 0.234, 0.51
      if (Math.trunc(monthsToPay) === 0 && !allowZeroDepreciation) {
        //Modified validation for months to pay to allow zero
        swal({
          icon: "warning",
          title: "Invalid Months to Pay",
          text: "Months to Pay cannot be less than one",
        }).then(() => setMonthsToPay(0));
        return;
      }

      const parsedDepreciation = parseFloat(
        String(depreciationAmount).replace(/,/g, ""),
      );
      const parsedTotalCost = parseFloat(String(totalCost).replace(/,/g, ""));

      if (parsedDepreciation > parsedTotalCost) {
        swal({
          icon: "warning",
          title: "Amount Exceeded",
          text: "The Depreciation Amount cannot be greater than the Total Cost.",
        });
        return;
      }

      handleCheckForecast();
    }

    setValidated(true);
  };

  // Handle creation of new fixed asset
  const handleSaveFixedAsset = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    let formatMonthsToPay = String(monthsToPay).replace(/,/g, "");
    let formatDepreciationAmount = String(depreciationAmount).replace(/,/g, "");
    const form = e.currentTarget;

    // Validate the fixed asset check form
    if (form.checkValidity() === false) {
      setValidatedFormCheck(true);
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
      return;
    }

    // Replaced by checkCutoffDate (utils/cutoffDateValidation): dateValidation's
    // warning was commented out, so it never warned
    // dateValidation(date, setDate);
    // Posted cutoff: the Depreciation Date can't be used, stop here.
    // No cutoff yet: warning only, the user can still confirm.
    const cutoffStatus = await checkCutoffDate(date, "Depreciation Date");
    if (cutoffStatus === "posted") return;

    swal({
      title: "Are you sure?",
      text: "Create this new fixed asset?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        axios
          .post(`${BASE_URL}/fixedasset/createFixedAsset`, {
            transactionCode,
            date,
            expenses_id,
            costPerUnit: removeComma(costPerUnit),
            quantity: removeComma(quantity),
            totalCost,
            monthsToPay: formatMonthsToPay,
            depreciationAmount: formatDepreciationAmount,
            remarks,
            forecast,
            userLoggedID,
            productName,
            currencyId,
            currencyRate,
          })
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Success",
                text: "Fixed asset created successfully",
                icon: "success",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              }).then(() => {
                navigate("/accounting/fixedAsset");
              });
            } else {
              swal({
                title: "Something Went Wrong",
                text: "Please contact your support immediately",
                icon: "error",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              });
            }
          })
          .catch((error) => {
            if (error.response && error.response.status == 409) {
              swal({
                title: "Oopps!",
                text: "Action is prohibited because the Depreciation Date has already passed the posted cutoff.",
                icon: "error",
                button: true,
              });
            }
          });
      } else {
        setShow(true);
      }
    });
  };

  // Update fixed asset record and associated checks
  const handleUpdateFixedAsset = (e) => {
    e.preventDefault();
    e.stopPropagation();

    let formatMonthsToPay = String(monthsToPay).replace(/,/g, "");
    let formatDepreciationAmount = String(depreciationAmount).replace(/,/g, "");
    const form = e.currentTarget;

    // Validate the fixed asset check form
    if (form.checkValidity() === false) {
      setValidatedFormCheck(true);
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
      return;
    }

    swal({
      title: "Update this fixed asset?",
      text: "",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) {
        axios
          .post(`${BASE_URL}/fixedasset/updateFixedAsset`, {
            date,
            expenses_id,
            costPerUnit: removeComma(costPerUnit),
            quantity: removeComma(quantity),
            totalCost,
            monthsToPay: formatMonthsToPay,
            depreciationAmount: formatDepreciationAmount,
            remarks,
            forecast,
            id,
            userLoggedID,
            productName,
            currencyId,
          })
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Success",
                text: "Fixed asset updated successfully",
                icon: "success",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              }).then(() => {
                fetchFixedAsset(id);
                setEdit(false);
                setShow(false);
                // Update edit mode checkbox after successful save
                setAllowZeroDepreciation(
                  formatMonthsToPay == 0 && formatDepreciationAmount == 0,
                );
              });
            } else {
              swal({
                title: "Something Went Wrong",
                text: "Please contact your support immediately",
                icon: "error",
                buttons: false,
                timer: 2000,
                dangerMode: true,
              });
            }
          })
          // --- ADDED CATCH BLOCK FOR POST-APPROVAL ERRORS ---
          .catch((error) => {
            if (error.response && error.response.status === 403) {
              swal({
                title: "Action Prohibited",
                text: error.response.data.message,
                icon: "error",
                button: true,
              });
            } else {
              swal({
                title: "Something Went Wrong",
                text: "Please contact your support immediately",
                icon: "error",
                button: true,
              });
            }
          });
        // --------------------------------------------------
      } else {
        setShow(true);
      }
    });
  };

  // Marks a fixed asset forecast as paid
  const handlePayForecast = async (fixedAssetForecastId) => {
    try {
      const payForecast = await swal({
        icon: "warning",
        title: "Confirm Forecast Payment",
        text: "Are you sure you want to proceed with this fixed asset forecast payment?",
        buttons: true,
        dangerMode: true,
      });

      if (payForecast) {
        const res = await axios.patch(
          `${BASE_URL}/fixedasset/forecast/${fixedAssetForecastId}/payments`,
        );

        if (res.status === 200) {
          await swal({
            icon: "success",
            title: "Payment Successful",
            text: "The fixed asset forecast has been marked as paid.",
          });

          fetchFixedAsset(id);
        }
      }
    } catch (error) {
      if (error.response.status === 401) {
        swal({
          icon: "error",
          // title: "Please contact your support",
          title: error.response.data.title,
          text: error.response.data.message,
        }); // test 1
      } else {
        console.log(error);
      }
    }
  };

  // Marks a fixed asset forecast as passed
  const handlePassForecast = async (fixedAssetId, fixedAssetForecastId) => {
    try {
      const passForecast = await swal({
        icon: "warning",
        title: "Mark Forecast as Passed?",
        text: "This will mark the selected fixed asset forecast as passed and generate a new forecast month. Do you want to continue?",
        buttons: true,
        dangerMode: true,
      });

      if (passForecast) {
        const res = await axios.patch(
          `${BASE_URL}/fixedasset/forecast/${fixedAssetForecastId}`,
          {
            fixedAssetId,
          },
        );

        if (res.status === 200) {
          await swal({
            icon: "success",
            title: "Forecast Passed",
            text: "The fixed asset forecast has been marked as passed.",
          });

          fetchFixedAsset(id);
        }
      }
    } catch (error) {
      if (error.response.status === 401) {
        swal({
          icon: "error",
          // title: "Please contact your support",
          title: error.response.data.title,
          text: error.response.data.message,
        }); // test 1
      } else {
        console.log(error);
      }
    }
  };

  //commented kasi nag eeror pag malaking value na ang cost (nag add ng guard para d mag error at max months is 1000 lang)
  // const handleMonthsToPay = (value) => {
  //   if (value === ".") {
  //     setMonthsToPay((prev) => prev + ".");
  //   }
  //   let inputValue = String(value || 0).replace(/[^0-9.]/g, "");

  //   let [integerPart, decimalPart] = inputValue.split(".");

  //   if (integerPart) {
  //     integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  //   }

  //   let formattedValue =
  //     decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

  //   let formatAmount = inputValue.replace(/,/g, "");
  //   // Convert to number and format with commas
  //   let numericValue = parseFloat(formatAmount);

  //   setMonthsToPay(formattedValue);
  //   // Calculate depreciation amount based on months to pay
  //   if (totalCost && numericValue > 0) {
  //     const total = totalCost / numericValue;

  //     let [integerPart, decimalPart] = String(total).split(".");

  //     if (integerPart) {
  //       integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  //     }

  //     let formattedValue =
  //       decimalPart !== undefined
  //         ? `${integerPart}.${decimalPart}`
  //         : integerPart;

  //     setDepreciationAmount(formattedValue);
  //   } else {
  //     setDepreciationAmount(0);
  //   }
  // };

  const handleMonthsToPay = (value) => {
    // remove non-numeric chars
    let rawValue = String(value || "").replace(/[^0-9]/g, "");

    // allow clearing
    if (!rawValue) {
      setMonthsToPay("");
      setMonthsError("");
      return;
    }

    const numericValue = parseInt(rawValue, 10);

    // 🚫 exceed limit → do NOT update months
    if (numericValue > MAX_MONTHS_TO_PAY) {
      setMonthsError(`Maximum allowed months to pay is ${MAX_MONTHS_TO_PAY}.`);
      return;
    }

    // ✅ valid
    setMonthsError("");

    // format with commas
    const formattedMonths = rawValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    setMonthsToPay(formattedMonths);

    // calculate depreciation amount
    if (totalCost && numericValue > 0) {
      const depreciation = totalCost / numericValue;

      let [intPart, decPart] = String(depreciation).split(".");
      if (intPart) {
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }

      setDepreciationAmount(
        decPart !== undefined ? `${intPart}.${decPart}` : intPart,
      );
    }
  };

  const formatWithCommas = (value) => {
    if (value === "" || value === null) return "";

    let [integer, decimal] = String(value).split(".");
    integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return decimal !== undefined ? `${integer}.${decimal}` : integer;
  };

  //commented kasi nag eeror pag malaking value na ang cost (nag add ng guard para d mag error at max months is 1000 lang)
  // const handleDepriciationAmount = (value) => {
  //   if (value == ".") {
  //     setMonthsToPay((prev) => prev + ".");
  //   }
  //   let inputValue = String(value || 0).replace(/[^0-9.]/g, "");

  //   let [integerPart, decimalPart] = inputValue.split(".");

  //   if (integerPart) {
  //     integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  //   }
  //   let formattedValue =
  //     decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

  //   let formatAmount = inputValue.replace(/,/g, "");
  //   // Convert to number and format with commas
  //   let numericValue = parseFloat(formatAmount);

  //   setDepreciationAmount(formattedValue);
  //   // Calculate months to pay based on depreciation amount
  //   if (numericValue > 0) {
  //     const total = Math.ceil(totalCost / numericValue);

  //     setMonthsToPay(total);
  //     initializeFixedAssetChecks(total, formattedValue, totalCost);
  //   } else {
  //     setMonthsToPay(0);
  //   }
  // };

  const handleDepriciationAmount = (value) => {
    let rawValue = String(value || "").replace(/[^0-9.]/g, "");

    const numericValue = parseFloat(rawValue);
    const parsedTotalCost = removeComma(totalCost);

    if (numericValue > parsedTotalCost) {
      swal({
        icon: "warning",
        title: "Amount Exceeded",
        text: "The Depreciation Amount cannot be greater than the Total Cost.",
      });

      // Clear the invalid values
      setDepreciationAmount("");
      setMonthsToPay("");
      setDepreciationError("");
      return;
    }

    // format for display
    const formattedValue = formatWithCommas(rawValue);

    // ✅ always update depreciation amount (with commas)
    setDepreciationAmount(formattedValue);

    if (!numericValue || numericValue <= 0 || !totalCost) {
      setDepreciationError("");
      return;
    }

    const calculatedMonths = Math.ceil(totalCost / numericValue);

    // 🚫 exceeds max → do NOT set months
    if (calculatedMonths > MAX_MONTHS_TO_PAY) {
      setDepreciationError(
        `Cannot calculate value. Maximum months to pay is ${MAX_MONTHS_TO_PAY}.`,
      );
      return;
    }

    // ✅ valid
    setDepreciationError("");
    setMonthsToPay(calculatedMonths);
  };

  const fetchCurrency = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/currency/fetchCurrency`);
      setCurrencyList(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  const dateToday = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const year = today.getFullYear();

    return `${year}-${month}-${day}`;
  };

  // Reusable function for numbers
  const handleAmountChange = (value, setState) => {
    if (value === ".") {
      setState((prev) => (prev.includes(".") ? value : prev + "."));
      return;
    }

    // Remove non-numeric and non-dot characters
    let inputValue = value.replace(/[^0-9.]/g, "");

    // Split integer and decimal
    let [integerPart, decimalPart] = inputValue.split(".");

    // ✅ Remove leading zeros from integer part only (keep at least one digit)
    if (integerPart) {
      integerPart = integerPart.replace(/^0+(?!$)/, "");
      // Add commas
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    // Rebuild formatted value
    let formattedValue =
      decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

    setState(formattedValue);
  };

  // Replaced by checkCutoffDate (utils/cutoffDateValidation): its "no cutoff"
  // warning was commented out, so it never warned.
  // const dateValidation = async (selectedDate, clearField) => {
  //   try {
  //     const res = await axios.get(`${BASE_URL}/cutoff/dateValidation`, {
  //       params: {
  //         date: selectedDate,
  //       },
  //     });
  //     // if (res.data == false) {
  //     //   swal({
  //     //     icon: "error",
  //     //     title: "Invalid Date Selection",
  //     //     text: "Please Create Cutoff for this Date",
  //     //   }).then(() => {
  //     //     clearField("");
  //     //   });
  //     // }
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  const handleOpenPaymentModal = () => setShowPaymentModal(true);
  const handleClosePaymentModal = (clearFields) => {
    setShowPaymentModal(false);
    clearFields();
  };

  const handleOpenJournalModal = () => setShowJournalModal(true);
  const handleCloseJournalModal = () => setShowJournalModal(false);

  useEffect(() => {
    handleMonthsToPay(monthsToPay);
  }, [totalCost]);

  useEffect(() => {
    setDate(dateToday());
    fetchCurrency();
    // getSubject2();
    // getSubject3(currencyId);
  }, []);

  useEffect(() => {
    fetchFixedAsset(id);
  }, [id]);

  // useEffect(() => {
  //   if (!id) fetchLastCode();
  //   // fetchAssets();
  //   calculateTotalCost();
  // }, [costPerUnit, quantity, monthsToPay, totalCost]);

  useEffect(() => {
    if (!id) {
      fetchLastCode();
    }
  }, [id]);

  useEffect(() => {
    calculateTotalCost();
  }, [costPerUnit, quantity]);

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex title-custom">
          <span className="fs-3">
            <Link to="/accounting/fixedAsset" className="text-dark me-2">
              <i class="fa-solid fa-arrow-left"></i>
            </Link>
            {id ? "PREVIEW FIXED ASSET" : "ADD FIXED ASSET"}
          </span>
        </div>
        {status === "Approved" && (
          <div>
            <button
              className="btn btn-primary"
              onClick={handleOpenPaymentModal}
              disabled={expensesValue <= 0} // if expenses is 0 then disable na payment
            >
              Create Payment
            </button>
          </div>
        )}
      </div>

      {/* Render Product Current Value and Months Remaining cards when fixed asset is approved */}
      {status === "Approved" && (
        <FixedAssetPreviewWidget
          fixedAssetId={id}
          status={status}
          currencyName={currencyName}
          totalCost={totalCost}
          monthsToPay={monthsToPay}
          handleOpenJournalModal={handleOpenJournalModal}
          handleOpenForecastModal={handleCheckForecast}
          refreshTrigger={refreshTrigger}
        />
      )}

      {/* Fixed asset form */}
      <Form noValidate validated={validated} onSubmit={openModal}>
        <div className="container-fluid mt-4">
          <div className="row mb-2">
            <div className="col-sm">
              <span>Item Code</span>
              <input
                type="text"
                name=""
                id=""
                required
                className="form-control p-3"
                readOnly
                onChange={(e) => setTransactionCode(e.target.value)}
                value={transactionCode}
              />
            </div>
            <div className="col-sm">
              <span>Depreciation Date</span>
              <CustomDatePicker
                selected={date ? new Date(date) : ""}
                handleDateChange={(date) => {
                  if (!date) {
                    setDate("");
                    return;
                  }
                  setDate(date);
                  // Replaced by checkCutoffDate (utils/cutoffDateValidation)
                  // dateValidation(date, setDate);
                  // TypedDateInput only sends complete dates
                  checkCutoffDate(date, "Depreciation Date");
                }}
                setter={setDate}
                CustomInput={TypedDateInput}
                isRequired={true}
                validated={validated}
                // Replaced by checkCutoffDate (utils/cutoffDateValidation)
                // dateValidation={dateValidation}
                dateValidation={(date) => {
                  checkCutoffDate(date, "Depreciation Date");
                }}
                iconTopOffset={"1.3rem"}
                padding={"p-3"}
                disabled={!edit}
              />
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-sm">
              <span>Product Name</span>
              <input
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className="form-control p-3"
                required
                disabled={!edit}
              />
            </div>

            <div className="col-sm">
              <span>Currency</span>
              <select
                className="form-select p-3"
                value={currencyId}
                onChange={(e) => {
                  const currency = currencyList.find(
                    (item) => item.id === e.target.value,
                  );
                  setCurrencyName(currency.currency_name);
                  setCurrencyId(e.target.value);
                }}
                required
                disabled={!edit}>
                <option value="" disabled>
                  Select Currency
                </option>
                {currencyList.map((data) => (
                  <option key={data.id} value={data.id}>
                    {data.currency_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-sm">
              <span>Quantity</span>
              <input
                type="text"
                value={quantity}
                // prettier-ignore
                onChange={(e) => {
                  const value = e.target.value;
                  const dotIndex = value.indexOf(".");
                  if (dotIndex !== -1 && value.length - dotIndex - 1 > 6) return;
                  handleAmountChange(value, setQuantity);
                }}
                min={0}
                className="form-control"
                style={{
                  padding: `1rem ${validated ? "2rem" : "1rem"} 1rem 1rem`,
                }}
                required
                disabled={!edit}
              />
            </div>
            <div className="col-sm">
              <div className="row">
                <div className="col-sm mb-2 z-0">
                  <span>Cost Per Unit</span>
                  <div className="input-group mb-2">
                    <input
                      type="text"
                      value={
                        ///formatCurrency(costPerUnit, currencyName) }
                        isCostPerUnitFocused
                          ? costPerUnit
                          : formatCurrency(
                              removeComma(costPerUnit),
                              currencyName,
                            )
                      }
                      onFocus={() => setIsCostPerUnitFocused(true)}
                      onBlur={() => setIsCostPerUnitFocused(false)}
                      // onInput={onInputFloat}
                      // prettier-ignore
                      onChange={(e) => {
                        const value = e.target.value;
                        const dotIndex = value.indexOf(".");
                        if (dotIndex !== -1 && value.length - dotIndex - 1 > 2) return;
                        handleAmountChange(value, setCostPerUnit);
                      }}
                      className="form-control p-3"
                      id="inlineFormInputGroup"
                      required
                      disabled={!edit}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-sm">
              <div className="row">
                <div className="col-sm">
                  <span>Total Cost</span>
                  <input
                    type="text"
                    required
                    onInput={onInputFloat}
                    className="form-control p-3"
                    id="inlineFormInputGroup"
                    value={formatCurrency(totalCost, currencyName)}
                    placeholder="0.00"
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-sm mb-2">
              <span>
                Months to Pay{" "}
                {monthsError && (
                  <span className="text-danger small">{monthsError}</span>
                )}
              </span>{" "}
              {depreciationError && (
                <span className="text-danger small">{depreciationError}</span>
              )}
              <div className="input-group mb-2">
                <input
                  type="text"
                  onInput={onInputFloat}
                  required
                  className="form-control p-3"
                  id="inlineFormInputGroup"
                  placeholder="0"
                  value={monthsToPay}
                  readOnly={allowZeroDepreciation}
                  onChange={(e) => {
                    if (!allowZeroDepreciation) {
                      // Only allow changes when checkbox is unchecked
                      const cleanedValue = String(e.target.value).replace(
                        /^0+,|^0+/,
                        "",
                      ); // Remove leading zeros and comma

                      handleMonthsToPay(cleanedValue);
                    }
                  }}
                  // Prevent Decimal input
                  onKeyDown={(e) => {
                    if (e.key === ".") e.preventDefault();
                  }}
                  disabled={!edit}
                />
              </div>
            </div>
            <div className="col-sm mb-2">
              <span>
                Depreciation Amount{" "}
                {depreciationError && (
                  <span className="text-danger small">{depreciationError}</span>
                )}
              </span>
              <div className="input-group mb-2">
                <div className="input-group-prepend">
                  <div className="input-group-text h-100">{currencyName}</div>
                </div>
                <input
                  type="text"
                  required
                  className="form-control p-3"
                  id="inlineFormInputGroup"
                  placeholder="0.00"
                  value={depreciationAmount}
                  readOnly={allowZeroDepreciation}
                  onChange={(e) => {
                    if (!allowZeroDepreciation) {
                      // Only allow changes when checkbox is unchecked
                      const cleanedValue = String(e.target.value).replace(
                        /^0+,|^0+/,
                        "",
                      ); // Remove leading zeros and comma
                      handleDepriciationAmount(cleanedValue);
                    }
                  }}
                  onInput={onInputFloat}
                  disabled={!edit}
                />
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="allowZeroDepreciationCheck"
                  checked={allowZeroDepreciation}
                  onChange={(e) => {
                    setAllowZeroDepreciation(e.target.checked);
                    // When checkbox is checked, set values to 0
                    if (e.target.checked) {
                      setDepreciationAmount("0");
                      setMonthsToPay("0");
                    }
                  }}
                  disabled={!edit}
                />
                <label
                  className="form-check-label"
                  htmlFor="allowZeroDepreciationCheck">
                  Allow zero depreciation amount
                </label>
              </div>
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-sm mb-2">
              <span>Remarks</span>
              <textarea
                cols="5"
                rows="5"
                className="form-control p-2"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={!edit}></textarea>
            </div>
          </div>
          <div className="row mt-4">
            <div className="col-sm"></div>
            <div className="col-sm"></div>
            <div className="col-sm"></div>
            <div className="col-sm d-flex flex-row align-items-end mb-2 w-100 gap-3">
              {/* For creating fixed asset */}
              {/* For creating a new fixed asset */}
              {!status && (
                <>
                  <Button
                    variant="secondary"
                    className="w-100"
                    type="button"
                    onClick={() => navigate("/accounting/fixedAsset")}>
                    Cancel
                  </Button>
                  <Button className="btn btn-primary w-100" type="submit">
                    Proceed
                  </Button>
                </>
              )}

              {/* For viewing/editing an existing fixed asset */}
              {status && (
                <>
                  {edit ? (
                    <>
                      {/* EDIT MODE BUTTONS */}
                      <Button
                        variant="secondary"
                        className="w-100"
                        type="button"
                        onClick={() => {
                          swal({
                            icon: "warning",
                            title: "Are you sure?",
                            text: "Your changes will not be saved.",
                            buttons: true,
                            dangerMode: true,
                          }).then((confirm) => {
                            if (confirm) fetchFixedAsset(id);
                          });
                        }}>
                        Cancel
                      </Button>
                      <Button className="btn btn-primary w-100" type="submit">
                        {status === "Approved"
                          ? "Update Fixed Asset"
                          : "Add Checks"}
                      </Button>
                    </>
                  ) : (
                    <div className="d-flex w-100 flex-row gap-3">
                      {/* VIEW MODE BUTTONS */}

                      {/* 1. Edit Button (Shown if user has proper auth) */}
                      {((status === "Pending" &&
                        authrztn?.includes("FixedAssets-Edit")) ||
                        (status === "Approved" &&
                          authrztn?.includes(
                            "FixedAssets-PostApprovalEdit",
                          ))) && (
                        <Button
                          variant="primary"
                          className="w-100"
                          type="button"
                          onClick={() => setEdit(true)}
                          disabled={isCutoffPosted}>
                          Edit
                        </Button>
                      )}

                      {/* 2. Proceed Button (Only for Pending Assets) */}
                      {status === "Pending" && (
                        <Button
                          variant="success"
                          className="w-100"
                          type="submit">
                          Proceed
                        </Button>
                      )}

                      {/* 3. Check Forecast Button (Only for Approved Assets - Shows Depreciate & Pass) */}
                      {status === "Approved" && (
                        <Button
                          variant="success"
                          className="w-100"
                          type="button"
                          onClick={handleCheckForecast}>
                          Check Forecast
                        </Button>
                      )}

                      {/* 4. Delete Button (Shown if user has proper auth) */}
                      {status !== "Deleted" &&
                        authrztn?.includes("FixedAssets-Delete") && (
                          <Button
                            variant="danger"
                            className="w-100"
                            type="button"
                            onClick={handleDeleteFixedAsset}
                            disabled={isCutoffPosted}>
                            Delete
                          </Button>
                        )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Form>

      {/* Payment History */}
      <FixedAssetJournalModal
        show={showJournalModal}
        onHide={handleCloseJournalModal}
        fixedAssetId={id}
      />

      {/* Revised Forecast Modal (Create/Edit/ApprovalRejection/Viewing) */}
      <FixedAssetForecastModal
        show={show}
        onHide={handleClose}
        status={status}
        edit={edit}
        id={id}
        forecast={forecast}
        allowZeroDepreciation={allowZeroDepreciation}
        currencyName={currencyName}
        // currencyId={currencyId}
        // selectedFixedAsset={selectedFixedAsset}
        handlePayForecast={handlePayForecast}
        handlePassForecast={handlePassForecast}
        handleUpdateFixedAsset={handleUpdateFixedAsset}
        handleSaveFixedAsset={handleSaveFixedAsset}
        handleApproveFixedAsset={handleApproveFixedAsset}
        handleRejectFixedAsset={handleRejectFixedAsset}
        validatedFormCheck={validatedFormCheck}
        refreshTrigger={refreshTrigger}
      />

      {/* Create Payment */}
      <FixedAssetPaymentModal
        fixedAssetInfo={{
          id,
          transactionNumber: transactionCode,
          currencyName,
          currencyRate,
          transactionDate: date,
        }}
        showPaymentModal={showPaymentModal}
        handleClosePaymentModal={handleClosePaymentModal}
        CustomInput={TypedDateInput}
        currencyId={currencyId}
        expensesValue={expensesValue}
        fetchFixedAsset={fetchFixedAsset}
      />
    </div>
  );
};

export default CreateFixedAsset;
